<script setup>

const target = ref(null); // Correctly define the target ref

const setRef = (el) => {
  target.value = el;
};
const {$_, $formatNumber} = useNuxtApp()
const router = useRouter()

const userStore = useUserStore()

async function fetchNotifications() {
  userStore.fetchTopUnreadNotifications()
}

let pollingInterval = null

onMounted(() => {
  userStore.markAllTopNotificationsAsRead()

  pollingInterval = setInterval(async () => {
    fetchNotifications()
  }, 1000 * 60)
})

onUnmounted(() => clearTimeout(pollingInterval))

async function navigateNotification(notificationId = null, notificationType = null) {
  if (!notificationId) {
    return
  }
  await userStore.markNotificationAsRead(notificationId)
  await userStore.fetchTopUnreadNotifications()
  router.push(getUrlByType(notificationType))
}

const maxPages = ref(1)
const page = ref(1)

function notificationHeader(notificationType) {
  switch (notificationType) {
    case 'Invoice':
      return $_('notification-bell.invoice.title')
    case 'WasteCollection':
      return $_('notification-bell.waste_collection.title')
    case 'WasteCollectionYardReport':
      return $_('notification-bell.waste_collection_yard_report.title')
    case 'WasteLimitReached':
      return $_('notification-bell.waste_limit_reached.title')
    default:
      return ''
  }
}

const allNotifications = ref(false)
const notifications = computed(() => {
    return userStore.topUnreadNotifications
})

function getUrlByType(notificationType) {
  switch (notificationType) {
    case 'Invoice':
      return '/profile/invoices'
    case 'WasteCollection':
      return '/my-waste/all'
    case 'WasteCollectionYardReport':
      return '/my-waste/all'
    case 'WasteLimitReached':
      return '/collection-yard'
    default:
      return '/profile/notifications'
  }
}

function getTextColor(backgroundColor) {
  if (!backgroundColor) return '#000'; // Default to black if no color is provided
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000; // Calculate brightness
  return brightness > 125 ? '#000' : '#fff'; // Use white text for dark backgrounds
}

const emit = defineEmits(['close'])
const close = () => {
  emit('close')
}
</script>
<template>
  <div class="content">
    <div class="head">
      <h1 class="text-xl font-extrabold text-gray-800">{{ $_('notifications.title', 'Upozornění') }}</h1>
    </div>
    <p v-if="notifications.length === 0"
       class="w-full px-[22px] py-4 text-center text-sm font-semibold">
      {{ $_('notifications.unread.no_notifications', 'Žádné nové upozornění') }}
    </p>
    <ul>
      <template v-for="(notification, notificationIndex) in notifications" :key="notification.id">
        <li
          :ref="page < maxPages && allNotifications && notifications.length - 1 === notificationIndex ? setRef : null">
          <NuxtLink class="transition rounded-lg hover:bg-gray-100 notification"
                    @click="() => navigateNotification(notification.id, notification.type)">
            <div>
              <svg v-if="notification.type === 'WasteCollection'" xmlns="http://www.w3.org/2000/svg"
                   width="20" height="21" fill="none">
                <g fill="#808285" clip-path="url(#a)">
                  <path d="M7.065 10.77h1.812v6.1H7.065v-6.1ZM11.122 10.77h1.812v6.1h-1.812v-6.1Z"/>
                  <path
                    d="M3.481 3.315H6.16v-.383C6.16 1.592 7.274.5 8.64.5h2.757c1.367 0 2.482 1.093 2.482 2.432v.383h2.639C17.885 3.315 19 4.407 19 5.747v3.247h-1.576v9.074c0 1.34-1.114 2.432-2.48 2.432H5.056c-1.367 0-2.482-1.093-2.482-2.432V8.994H1V5.747c0-1.34 1.115-2.432 2.481-2.432Zm1.576 15.409h9.886c.37 0 .67-.294.67-.656V8.994H4.387v9.074c0 .362.3.656.67.656Zm6.341-16.448H8.641c-.37 0-.67.294-.67.656v.383h4.097v-.383c0-.362-.3-.656-.67-.656Zm5.79 3.471c0-.363-.3-.656-.67-.656H3.482c-.37 0-.67.293-.67.656v1.471h14.377v-1.47Z"/>
                </g>
                <defs>
                  <clipPath id="a">
                    <path fill="#fff" d="M20 .5H0v20h20z"/>
                  </clipPath>
                </defs>
              </svg>
              <svg v-else-if="notification.type === 'Invoice'" xmlns="http://www.w3.org/2000/svg"
                   width="20" height="21" fill="none" viewBox="0 0 20 21">
                <g fill="#808285" clip-path="url(#clip0_432_10397)">
                  <path
                    d="M17.945.567a.883.883 0 0 0-.974.2l-1.9 1.927L13.166.767a.888.888 0 0 0-1.267 0L10 2.694 8.1.767A.91.91 0 0 0 7.467.5a.889.889 0 0 0-.633.267L4.93 2.694 3.029.767a.885.885 0 0 0-.973-.2.918.918 0 0 0-.556.842v18.182c0 .503.4.909.896.909h15.208a.901.901 0 0 0 .896-.91V1.41a.918.918 0 0 0-.555-.843Zm-1.237 18.115H3.292V3.603l1.003 1.018a.889.889 0 0 0 1.267 0l1.9-1.927 1.905 1.927a.882.882 0 0 0 1.266 0l1.9-1.927 1.9 1.927a.888.888 0 0 0 1.265 0l1.004-1.018v15.079h.006Z"/>
                  <path
                    d="M14.02 14.44H5.98a.901.901 0 0 0-.896.908c0 .503.4.91.896.91h8.04a.901.901 0 0 0 .896-.91.905.905 0 0 0-.896-.909ZM5.98 12.621h4.456a.901.901 0 0 0 .896-.909c0-.503-.4-.909-.896-.909H5.98a.901.901 0 0 0-.896.91c0 .502.4.908.896.908ZM5.98 8.985h4.456a.901.901 0 0 0 .896-.91c0-.502-.4-.908-.896-.908H5.98a.901.901 0 0 0-.896.909c0 .503.4.909.896.909Z"/>
                </g>
                <defs>
                  <clipPath id="clip0_432_10397">
                    <path fill="white" d="M0 .5h20v20H0z"/>
                  </clipPath>
                </defs>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="21" fill="none">
                <path fill="#808285"
                      d="M3.527 11.783c.211-.361.432-.737.647-1.107.202-.337.4-.674.606-1.016l.206-.347L7.31 10.84l.201-.159-1.154-5.439-5.571 1.31 2.666 1.756c-.038.063-.066.12-.099.174l-.915 1.54a633.052 633.052 0 0 0-2.009 3.393c-.544.925-.572 1.859-.075 2.773.507.939 1.305 1.415 2.37 1.425.409.005.822.005 1.23.005h1.891c.057 0 .108-.005.164-.01.024 0 .047-.005.07-.005l.132-.01v-1.848H4.874c-.685-.005-1.394-.005-2.093.005-.357.005-.658-.159-.812-.452a.967.967 0 0 1 .037-.963c.521-.838 1.028-1.709 1.52-2.551ZM19.61 13.458a35.554 35.554 0 0 0-.507-.842c-.225-.376-.455-.746-.69-1.127l-.366-.597-1.544.987.075.12c.146.236.287.472.428.703.342.563.661 1.098.994 1.637.212.346.226.678.043 1.01-.179.323-.446.472-.85.472h-.005c-1.285-.005-2.595-.005-3.862-.005h-1.84c-.037 0-.08 0-.127-.004v-3.09L7.446 16.77l3.703 3.73.22-.116V17.69h.189c.586 0 1.168 0 1.755.005 1.31.005 2.66.005 3.994-.01a2.68 2.68 0 0 0 2.332-1.42 2.813 2.813 0 0 0-.028-2.806ZM6.366 3.817l.005.072 1.436.934.131-.203.09-.14c.112-.182.225-.36.338-.543.225-.361.455-.732.685-1.097.249-.39.624-.544 1.037-.42.263.083.427.29.554.487.427.669.859 1.333 1.286 2.002.333.515.666 1.035 1 1.55.192.299.38.592.572.896l.192.303-2.374 1.67.225.106c.023.01.042.02.056.029.028.014.052.029.085.033.455.092.91.183 1.365.28 1.127.23 2.295.467 3.44.702.038.01.071.01.104.01a.266.266 0 0 0 .164-.048c.099-.067.122-.193.136-.274.113-.617.226-1.242.338-1.844.08-.438.164-.88.244-1.324l.23-1.29c.038-.207.07-.414.113-.63l.06-.337-2.684 1.858-.582-.91c-.262-.41-.53-.823-.784-1.227-.586-.92-1.187-1.868-1.806-2.783A2.637 2.637 0 0 0 9.849.5 2.595 2.595 0 0 0 7.67 1.612c-.324.457-.62.948-.906 1.425-.122.207-.253.419-.38.626a.174.174 0 0 0-.019.13c-.004.01 0 .014 0 .024Z"/>
              </svg>
            </div>
            <ul class="notificationContent">
              <li class="text-sm font-bold notificationHead text-gray-800"
                  :class="{ 'inactiveNotification': notification?.isRead }">
                {{ notificationHeader(notification.type) }}
                <div class="flex items-baseline gap-x-2"
                     v-if="notification?.data?.wasteName || notification?.data?.wasteColor">
                                    <span class="text-xs spanBackground" :style="{
                                        backgroundColor: notification?.data?.wasteColor,
                                        color: getTextColor(notification?.data?.wasteColor)
                                    }" v-if="notification?.data?.wasteName">{{
                                        notification.data.wasteName
                                      }}</span>
                </div>
              </li>
              <li class="text-sm "
                  :class="{ 'text-gray-600': notification?.isRead, 'text-gray-800': !notification?.isRead }">
                <div v-if="notification.type === 'Invoice'" class="flex items-center gap-x-2">
                  <span class="block h-2 w-2 flex-none rounded-full bg-red"></span>
                  <ButtonLink class="text-xs">{{ notification.data.invoice ?? 'undefined' }}
                  </ButtonLink>
                </div>
              </li>
              <li class="notificationText text-xs"
                  :class="{ 'text-gray-600': notification?.isRead, 'text-gray-800': !notification?.isRead }">
                <p v-if="notification.type === 'WasteCollection'">
                  {{
                    $_('notification-bell.waste_collection.text', {
                      bin: notification.data.wasteName,
                      weight: $formatNumber(notification.data.weight),
                    })
                  }}
                </p>
                <p v-if="notification.type === 'WasteCollectionYardReport'">
                  {{
                    $_('notification-bell.waste_collection_yard_report.text', {
                      weight: $formatNumber(notification.data.weight),
                      waste: notification.data.wasteName,
                    })
                  }}
                </p>
                <p v-if="notification.type === 'WasteLimitReached'">
                  {{
                    $_('notification-bell.waste_limit_reached.text', {
                      waste: notification.data.wasteName
                    })
                  }}
                </p>
                <p v-if="notification.type === 'Invoice'">
                  {{
                    $_('notification-bell.invoice.text', {
                      date: notification.data.recordedTs.format('DD. MM. YYYY')
                    })
                  }}
                </p>
                <p class="" v-if="notification?.data?.recordedTs">
                  {{ notification.data.recordedTs.format('DD. MM. YYYY HH:mm') }}
                </p>
              </li>
              <!-- <li class="text-sm text-gray-800" v-if="notification?.data?.recordedTs">
                  {{ timeSince(notification.createdTs) }}
              </li> -->
            </ul>
          </NuxtLink>
        </li>
      </template>
      <li class="border-t">
        <div class="px-4 hover:bg-gray-100">
          <button dusk="navbar-notifications"
                  class="flex w-full items-center gap-x-3 py-3 pl-4 transition hover:bg-gray-100"
                  @click.prevent="() => {
                        router.push({ path: '/profile/notifications' })
                        close()
                      }
                        ">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="28" fill="none">
              <path fill="#808285"
                    d="M4.222 16.188v-5.47c0-3.62 2.99-6.562 6.667-6.562V0h2.222v4.156c3.678 0 6.667 2.942 6.667 6.563v5.469c0 .836.483 1.788.994 2.794C21.378 20.169 22 21.399 22 22.75v1.094h-6.667v.875C15.333 26.529 13.84 28 12 28c-1.839 0-3.333-1.471-3.333-3.281v-.875H2V22.75c0-1.35.622-2.581 1.228-3.768.51-1.006.994-1.958.994-2.794Zm6.667 8.53c0 .602.5 1.095 1.111 1.095s1.111-.493 1.111-1.094v-.875H10.89v.875Zm8.672-3.062c-.189-.541-.478-1.11-.778-1.7-.605-1.187-1.227-2.418-1.227-3.768v-5.47c0-2.411-1.995-4.374-4.445-4.374H10.89c-2.45 0-4.445 1.963-4.445 4.375v5.469c0 1.35-.622 2.58-1.227 3.768-.3.59-.59 1.159-.778 1.7H19.56Z"/>
            </svg>

            <span class="text-sm font-bold text-gray-800">{{ $_('navigation.notifications') }}</span>
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>
<style scoped>
li:has(.toggle) {
  display: flex;
  justify-content: center;
}

.inactiveNotification {
  opacity: 0.6;
}

a {
  cursor: pointer;
}

ul {
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style-type: none;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toggles {
  display: flex;
  gap: 8px;
}

.notificationText {
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: 4px;
}

.spanBackground {
  padding: 2px 6px;
  border-radius: 999px;
  display: inline-block;
  text-align: center;
}

.notificationHead {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle {
  border-radius: 999px;
  padding: 8px 12px;
  transition: background-color 0.3s ease;
}

.notification {
  padding: 8px 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  transition: background-color 0.3s ease;
}

.toggle:not(.activeToggle):hover {
  background-color: #f1f1f1;
}

.activeToggle {
  color: rgb(26, 142, 26);
  background-color: hsl(87, 89%, 91%);
}

.notificationContent {
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

li:has(.spinner) {
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid rgb(26, 142, 26);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
